import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle, ArrowUpCircle, CheckCircle2, Loader, Megaphone, RefreshCw, X, XCircle,
} from 'lucide-react';
import { modpackConfigsApiService } from '../../services/modpackConfigsApi';
import {
  AnnounceUpdateResult,
  ClusterVersionBumpResponse,
  ModpackConfig,
  ModpackConfigEnv,
  UNCLUSTERED,
} from '../../types/modpackConfig';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import type { FleetStatus } from '../../hooks/useFleetStatus';
import { liveFor } from '../../hooks/useFleetStatus';
import { LiveStatusBadge, describeLive } from './LiveStatus';
import { cn } from '../../utils/cn';

interface UpdatePackModalProps {
  config: ModpackConfig;
  members: ModpackConfig[];
  env: ModpackConfigEnv;
  /** Live status, so an offline server is visible before anything is announced. */
  fleet?: FleetStatus | null;
  onClose: () => void;
  /** Called once the versions are written, whether or not the announcement went out. */
  onUpdated: () => Promise<void> | void;
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

/** `1.1.3` reads as `v1.1.3`; something already prefixed is left alone. */
const label = (version: string) =>
  version.trim().slice(0, 1).toLowerCase() === 'v' ? version.trim() : `v${version.trim()}`;

/**
 * Updating a pack, as one step.
 *
 * This is /modpackupdate: staff update the pack files on every server in the
 * cluster, then run one command that records the version everywhere and
 * announces it. Argus can only do the second half — it has no way to know
 * whether the files were actually updated — so the modal opens on that, as a
 * checklist naming each server, and will not proceed until each is ticked.
 *
 * The bump and the announcement are one action here, but not one transaction:
 * if any server refuses the new version the announcement is withheld, because
 * it pings everyone who plays the pack about an update that has not fully
 * landed. Re-running is safe on both halves.
 */
export const UpdatePackModal = ({ config, members, env, fleet, onClose, onUpdated }: UpdatePackModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const standalone = !config.cluster_name || config.cluster_name === UNCLUSTERED;
  const currentVersion = config.modpack_version ?? '';

  const [confirmed, setConfirmed] = useState<Record<number, boolean>>({});
  const [version, setVersion] = useState('');
  const [changelog, setChangelog] = useState('');

  const [phase, setPhase] = useState<'form' | 'bumping' | 'announcing' | 'done'>('form');
  const [error, setError] = useState<string | null>(null);
  const [bump, setBump] = useState<ClusterVersionBumpResponse | null>(null);
  const [announce, setAnnounce] = useState<AnnounceUpdateResult | null>(null);
  const [announceError, setAnnounceError] = useState<string | null>(null);

  const allConfirmed = useMemo(
    () => members.length > 0 && members.every((m) => confirmed[m.id]),
    [members, confirmed],
  );

  // What the network says about each server. An offline member is not a
  // reason to refuse -- the files may well have been updated while it was
  // down -- but it is a reason to look before announcing to every player.
  const liveness = useMemo(
    () => (fleet ? members.map((m) => ({ id: m.id, live: liveFor(fleet, m.server_address) })) : []),
    [fleet, members],
  );
  const notReachable = liveness.filter(({ live }) => {
    const { state } = describeLive(live);
    return state === 'offline' || state === 'maintenance' || state === 'coming_soon';
  });
  const versionOk = version.trim() !== '' && version.trim() !== currentVersion.trim();
  const canSubmit = allConfirmed && versionOk && phase === 'form';

  const toggleAll = (value: boolean) =>
    setConfirmed(Object.fromEntries(members.map((m) => [m.id, value])));

  const runAnnouncement = async (newVersion: string) => {
    setPhase('announcing');
    setAnnounceError(null);
    try {
      const result = await modpackConfigsApiService.announceClusterUpdate(
        config.cluster_parent as number,
        {
          old_version: currentVersion || '?',
          new_version: newVersion,
          ...(changelog.trim() ? { changelog_url: changelog.trim() } : {}),
        },
        env,
      );
      setAnnounce(result);
    } catch (err) {
      setAnnounceError(err instanceof Error ? err.message : 'The announcement could not be posted');
    } finally {
      setPhase('done');
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    setPhase('bumping');
    const newVersion = version.trim();
    try {
      const result = await modpackConfigsApiService.bumpClusterVersion(
        config.cluster_parent as number, newVersion, env,
      );
      setBump(result);
      await onUpdated();

      if (!result.success) {
        // Withheld on purpose. Announcing a cluster half of which still runs
        // the old version is worse than saying so.
        setPhase('done');
        return;
      }
      if (env === 'development') {
        // Nothing to announce: the dev table is a copy, not a fleet.
        setPhase('done');
        return;
      }
      await runAnnouncement(newVersion);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The version could not be recorded');
      setPhase('form');
    }
  };

  const busy = phase === 'bumping' || phase === 'announcing';

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-2xl max-md:max-w-[95vw]" role="dialog" aria-modal="true">
        <div className="modal-header">
          <div className="min-w-0">
            <h2 className="modal-title">Update {config.cluster_name && !standalone ? config.cluster_name : (config.modpack_full_name || config.server_address)}</h2>
            <p className="modal-subtitle">
              {standalone
                ? 'Records the new version and announces it.'
                : `Records the new version on all ${members.length} servers and announces it once.`}
            </p>
          </div>
          <button type="button" onClick={onClose} className="modal-close" aria-label="Close" disabled={busy}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body space-y-4">
          {phase !== 'done' && (
            <>
              <div className="alert alert-warning" role="status">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>
                  Argus records the version — <strong>it does not update the pack files.</strong> Do
                  that on every server first, then confirm each one here.
                </span>
              </div>

              <div className="subcard p-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-sm font-semibold text-white">
                    The pack files are already updated on:
                  </p>
                  <button
                    type="button"
                    onClick={() => toggleAll(!allConfirmed)}
                    className="btn-ghost text-xs"
                    disabled={busy}
                  >
                    {allConfirmed ? 'Clear all' : 'Confirm all'}
                  </button>
                </div>
                <div className="divide-y divide-glass-200/10">
                  {members.map((m) => (
                    <label key={m.id} className="flex items-center gap-3 py-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={Boolean(confirmed[m.id])}
                        onChange={(e) => setConfirmed((prev) => ({ ...prev, [m.id]: e.target.checked }))}
                        disabled={busy}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="font-mono text-sm text-white block truncate">{m.server_address}</span>
                        <span className="text-xs text-glass-400">
                          {m.region_name || 'no region'} · currently{' '}
                          <span className="font-mono tabular-nums">{m.modpack_version || '—'}</span>
                          {m.cluster_parent === m.id && ' · head'}
                        </span>
                      </span>
                      {fleet && (
                        <LiveStatusBadge live={liveFor(fleet, m.server_address)} className="shrink-0" />
                      )}
                    </label>
                  ))}
                </div>
                {notReachable.length > 0 && env === 'production' && (
                  <p className="text-xs text-amber-300 mt-2 flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      {notReachable.length === 1 ? 'One server is' : `${notReachable.length} servers are`} offline,
                      in maintenance or not open yet right now. The version can still be recorded, but the announcement
                      tells every player the update is live — make sure it will be.
                    </span>
                  </p>
                )}
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="update-version" className={LABEL_CLASS}>New version</label>
                  <input
                    id="update-version"
                    type="text"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder={currentVersion ? `currently ${currentVersion}` : '1.1.4'}
                    className="glass-input w-full font-mono"
                    disabled={busy}
                    required
                  />
                  {version.trim() && version.trim() === currentVersion.trim() && (
                    <p className="text-xs text-glass-500 mt-1">That is the current version.</p>
                  )}
                </div>
                <div>
                  <label htmlFor="update-changelog" className={LABEL_CLASS}>
                    Changelog link <span className="text-glass-500 font-normal">(optional)</span>
                  </label>
                  <input
                    id="update-changelog"
                    type="url"
                    value={changelog}
                    onChange={(e) => setChangelog(e.target.value)}
                    placeholder="https://…"
                    className="glass-input w-full"
                    disabled={busy}
                  />
                </div>
              </div>

              {env === 'production' ? (
                <div className="subcard p-3 space-y-1.5">
                  <p className="eyebrow">What gets announced</p>
                  <p className="text-sm text-white">
                    {standalone ? 'Our server has' : 'Our servers have'} now been updated to the latest
                    version! (<span className="font-mono">{label(currentVersion || '?')}</span> ⇒{' '}
                    <span className="font-mono">{version.trim() ? label(version) : '…'}</span>)
                  </p>
                  <p className="text-xs text-glass-400">
                    Pings the pack role and the update role in the pack-updates channel
                    {changelog.trim() ? ', with the changelog link' : ''}. A note is recorded against
                    each server and you are credited with {members.length} update{members.length === 1 ? '' : 's'}.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-glass-400">
                  Development: the version is recorded and nothing is announced.
                </p>
              )}
            </>
          )}

          {phase === 'done' && bump && (
            <div className="space-y-3">
              <div className={cn('alert', bump.success ? 'alert-success' : 'alert-danger')} role="status">
                {bump.success ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
                <span>
                  {bump.success
                    ? `Every server is now on ${bump.modpack_version}.`
                    : `${bump.failed.length} of ${members.length} servers did not take ${bump.modpack_version}. The announcement was not posted — fix these and run the update again; re-running is safe.`}
                </span>
              </div>

              {bump.failed.length > 0 && (
                <ul className="subcard p-3 divide-y divide-glass-200/10">
                  {bump.failed.map((f) => (
                    <li key={f.server_address} className="py-2 text-sm">
                      <span className="font-mono block">{f.server_address}</span>
                      <span className="text-xs text-glass-400 break-all">{f.error}</span>
                    </li>
                  ))}
                </ul>
              )}

              {bump.success && env === 'production' && (
                announce ? (
                  <div className="alert alert-success" role="status">
                    <Megaphone className="w-4 h-4 shrink-0" />
                    <span>
                      Announced.
                      {announce.notes_written > 0 && ` ${announce.notes_written} note${announce.notes_written === 1 ? '' : 's'} written.`}
                      {announce.points_awarded
                        ? ' Your update points were recorded.'
                        : ' Your update points could not be recorded — let an admin know.'}
                    </span>
                  </div>
                ) : (
                  <div className="alert alert-warning" role="alert">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span className="flex-1">
                      The versions are recorded but the announcement did not post
                      {announceError ? `: ${announceError}` : '.'}
                    </span>
                    <button
                      type="button"
                      onClick={() => runAnnouncement(bump.modpack_version)}
                      className="glass-button-secondary shrink-0"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry
                    </button>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {phase === 'done' ? (
            <button type="button" onClick={onClose} className="btn-primary ml-auto">Done</button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="glass-button-secondary" disabled={busy}>
                Cancel
              </button>
              <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="btn-primary">
                {busy
                  ? <Loader className="w-4 h-4 animate-spin" />
                  : <ArrowUpCircle className="w-4 h-4" />}
                {phase === 'bumping' ? 'Recording…'
                  : phase === 'announcing' ? 'Announcing…'
                  : env === 'production' ? 'Update and announce' : 'Update'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
